import { FC, ReactNode } from 'react';
import { generatePath } from 'react-router-dom';

import { FundCarouselItem } from '@/api/fund-views/types';
import GemIcon from '@/assets/svg/ui-elements/gem-filled.svg';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { PagePaths } from '@/routes/page-paths';
import { theme, Tooltip, Typography } from '@/shared/design-system';
import { TARGET_RETURN_LABEL, TRAILING_12M_RETURN_LABEL } from '@/utils/fund-returns';
import { formatIntOrMaxTwo, formatMillions, formatMoney } from '@/utils/numbers/formatters';

import {
  FundCardChip,
  FundCardChipIcon,
  FundCardChipsRow,
  FundCardChipTone,
  FundCardContent,
  FundCardLogo,
  FundCardLogoFallback,
  FundCardLogoWrapper,
  FundCardMinInvestRow,
  FundCardWrapper,
  getChipToneStyles,
} from './styled';

const FUND_NAME_TOOLTIP_LENGTH = 26;

interface Props {
  data: FundCarouselItem;
  action?: ReactNode;
}

export const FundCarouselCard: FC<Props> = ({ data, action }) => {
  const isMobile = useIsMobile();

  // Treats 0 as "no data", not as a realised 0% return: the backend maps a NULL
  // rolling return to 0 (`Number(null)`), so the two are indistinguishable here.
  const isNewFund = !data.returnInYear;
  const returnTone: FundCardChipTone =
    !isNewFund && (data.returnInYear as number) < 0 ? 'negative' : 'positive';
  const neutralTone = getChipToneStyles('neutral');
  const returnLabel =
    data.returnKind === 'target' ? TARGET_RETURN_LABEL : TRAILING_12M_RETURN_LABEL;

  // A fund with no disclosed minimum reads as "—" everywhere else (Under DD, the
  // detail stats, the admin table); "$0" here would state a minimum that is not one.
  const minInvestValue =
    data.minInvest != null ? formatMoney(Number(data.minInvest), data.fundCurrency) : '—';

  return (
    <FundCardWrapper
      to={generatePath(PagePaths.allFundsId, {
        identifier: data.slug,
      })}
      tabIndex={-1}
    >
      <FundCardLogoWrapper>
        {data.cardImageUrl ? (
          <FundCardLogo src={data.cardImageUrl} alt={`${data.fundName} logo`} />
        ) : (
          <FundCardLogoFallback />
        )}
      </FundCardLogoWrapper>

      <FundCardContent>
        <Tooltip
          title={data.fundName}
          side="top"
          disabled={data.fundName.length <= FUND_NAME_TOOLTIP_LENGTH}
        >
          <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={600} noWrap>
            {data.fundName}
          </Typography>
        </Tooltip>

        <FundCardChipsRow>
          <FundCardChip tone={returnTone}>
            {isNewFund ? (
              <FundCardChipIcon>
                <GemIcon width={20} height={20} />
              </FundCardChipIcon>
            ) : (
              <Typography
                variant={isMobile ? 'body2' : 'body1'}
                fontWeight={600}
                color={getChipToneStyles(returnTone).value}
                noWrap
              >
                {`${formatIntOrMaxTwo(data.returnInYear as number)}%`}
              </Typography>
            )}

            <Typography
              variant="body4"
              fontWeight={500}
              color={getChipToneStyles(returnTone).label}
              noWrap
            >
              {isNewFund ? 'New Fund' : returnLabel}
            </Typography>
          </FundCardChip>

          {data.fundAum !== null && (
            <FundCardChip tone="neutral">
              <Typography
                variant={isMobile ? 'body2' : 'body1'}
                fontWeight={600}
                color={neutralTone.value}
                noWrap
              >
                {`$${formatMillions(data.fundAum)}`}
              </Typography>

              <Typography variant="body4" fontWeight={500} color={neutralTone.label} noWrap>
                AUM
              </Typography>
            </FundCardChip>
          )}
        </FundCardChipsRow>

        <FundCardMinInvestRow>
          <Typography
            variant={isMobile ? 'body4' : 'body3'}
            fontWeight={500}
            color={theme.colors.text.secondary}
            noWrap
          >
            Min. Invest:
          </Typography>

          <Typography variant={isMobile ? 'body4' : 'body3'} fontWeight={500} noWrap>
            {minInvestValue}
          </Typography>
        </FundCardMinInvestRow>

        {action}
      </FundCardContent>
    </FundCardWrapper>
  );
};
